import React, { useEffect, useMemo, useRef, useState } from "react";
import { filterMaterialsCatalog } from "../helpers/materialSearch";

function formatCurrency(amount) {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  return `$${safe.toFixed(2)}`;
}

function formatMaterialSummary(material, { showPricing = true } = {}) {
  const parts = [];

  if (showPricing && material.unit_cost != null) {
    parts.push(formatCurrency(material.unit_cost));
  }

  if (material.sku) {
    parts.push(`SKU ${material.sku}`);
  }

  if (material.unit_of_measure) {
    parts.push(material.unit_of_measure);
  }

  if (material.material_type) {
    parts.push(material.material_type);
  }

  return parts.join(" · ");
}

export function MaterialSearchPicker({
  materials = [],
  value,
  onChange,
  disabled = false,
  label = "Material",
  placeholder = "Search Materials...",
  otherOptionLabel = "",
  otherOptionValue = "",
  showPricing = true,
  noResultsLabel = "No catalog products found.",
}) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedMaterial = useMemo(
    () => materials.find((item) => item.material_id === value),
    [materials, value],
  );

  const results = useMemo(
    () => filterMaterialsCatalog(materials, query),
    [materials, query],
  );

  useEffect(() => {
    if (!value) {
      setQuery("");
    }
  }, [value]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  // Defensive against mobile browsers (Android Chrome in particular) whose
  // layout viewport does not shrink when the on-screen keyboard opens: a
  // freshly-rendered results panel below the input can end up positioned
  // outside the area still visible above the keyboard. Pulling it into view
  // on every result change guarantees the estimator sees a reaction to each
  // keystroke instead of the search appearing to do nothing.
  useEffect(() => {
    if (open && query.trim() && resultsRef.current) {
      resultsRef.current.scrollIntoView?.({ block: "nearest" });
    }
  }, [open, query, results]);

  function handleSelect(material) {
    onChange?.(material.material_id);
    setQuery("");
    setOpen(false);
  }

  function handleClearSelection() {
    onChange?.("");
    setQuery("");
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleSelectOther() {
    onChange?.(otherOptionValue);
    setQuery("");
    setOpen(false);
  }

  if (selectedMaterial && !open) {
    return (
      <div className="cp-input-group">
        {label && <label className="cp-input-label">{label}</label>}

        <div
          style={{
            padding: "10px 12px",
            borderRadius: "10px",
            border: "1px solid rgba(201,168,76,0.35)",
            background: "rgba(201,168,76,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 auto" }}>
              <div style={{ fontWeight: 600, fontSize: "14px", color: "#e8dcc0" }}>
                {selectedMaterial.material_name}
              </div>
              {selectedMaterial.sku && (
                <div
                  style={{
                    color: "#e8dcc0",
                    opacity: 0.75,
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  SKU: {selectedMaterial.sku}
                </div>
              )}
              {selectedMaterial.unit_of_measure && (
                <div
                  style={{
                    color: "#e8dcc0",
                    opacity: 0.75,
                    fontSize: "13px",
                    marginTop: "2px",
                  }}
                >
                  Unit: {selectedMaterial.unit_of_measure}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleClearSelection}
              disabled={disabled}
              style={{
                flexShrink: 0,
                marginTop: "2px",
                padding: "4px 10px",
                borderRadius: "999px",
                border: "1px solid rgba(201,168,76,0.45)",
                background: "transparent",
                color: "#c9a84c",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: 1.2,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.5 : 1,
              }}
            >
              Change
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-input-group" ref={containerRef}>
      {label && <label className="cp-input-label">{label}</label>}

      <input
        ref={inputRef}
        className="cp-input"
        type="text"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />

      {open && otherOptionLabel && otherOptionValue && (
        <button
          type="button"
          onClick={handleSelectOther}
          disabled={disabled}
          style={{
            display: "block",
            width: "100%",
            marginTop: "8px",
            padding: "12px",
            borderRadius: "10px",
            border: "1px dashed rgba(201,168,76,0.35)",
            background: "rgba(201,168,76,0.06)",
            color: "#c9a84c",
            textAlign: "left",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            fontWeight: 600,
            fontSize: "14px",
          }}
        >
          {otherOptionLabel}
        </button>
      )}

      {open && query.trim() && (
        <div
          ref={resultsRef}
          style={{
            marginTop: "8px",
            borderRadius: "10px",
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(10,14,20,0.98)",
            maxHeight: "220px",
            overflowY: "auto",
          }}
        >
          {results.length === 0 ? (
            <div
              style={{
                padding: "12px",
                color: "#e8dcc0",
                opacity: 0.85,
                fontSize: "14px",
              }}
            >
              {noResultsLabel}
            </div>
          ) : (
            results.map((material) => (
              <button
                key={material.material_id}
                type="button"
                onClick={() => handleSelect(material)}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "12px",
                  border: "none",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  background: "transparent",
                  color: "white",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: "14px" }}>
                  {material.material_name}
                </div>
                <div
                  style={{
                    opacity: 0.75,
                    fontSize: "13px",
                    marginTop: "2px",
                  }}
                >
                  {formatMaterialSummary(material, { showPricing })}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
